import Axios from 'axios';
import { FixedFactorIngredientUsage, LotCoaSibling } from '@/models/Inventory';
import { CoaRangeBounds, UNBOUNDED_COA_RANGE, toNumberOrNull } from '@/util/activityFactor';

/**
 * The activity-factor settings the lot forms and the ingredient editor need, read once per screen.
 * Everything gates on `enabled`: when the client has not opted in, no COA or factor field is shown.
 */
export interface ActivityFactorSettings {
    enabled: boolean;
    bounds: CoaRangeBounds;
    decimalPlaces: number;
}

export const ACTIVITY_FACTOR_SETTINGS_OFF: ActivityFactorSettings = {
    enabled: false,
    bounds: UNBOUNDED_COA_RANGE,
    decimalPlaces: 4,
};

const SETTING_KEYS = [
    'EnableActivityFactorAdjustment',
    'ActivityFactorAssayMinPercent',
    'ActivityFactorAssayMaxPercent',
    'ActivityFactorLodMinPercent',
    'ActivityFactorLodMaxPercent',
    'ActivityFactorDecimalPlaces',
] as const;

/** Maps the raw name→value configuration response to typed settings; blank bounds mean no warning on that side. */
export function toActivityFactorSettings(values: Record<string, unknown> | null | undefined): ActivityFactorSettings {
    if (!values) return ACTIVITY_FACTOR_SETTINGS_OFF;
    const decimalPlaces = toNumberOrNull(values.ActivityFactorDecimalPlaces as number | string | null);
    return {
        enabled: values.EnableActivityFactorAdjustment === true || values.EnableActivityFactorAdjustment === 'true',
        bounds: {
            assayMin: toNumberOrNull(values.ActivityFactorAssayMinPercent as number | string | null),
            assayMax: toNumberOrNull(values.ActivityFactorAssayMaxPercent as number | string | null),
            lodMin: toNumberOrNull(values.ActivityFactorLodMinPercent as number | string | null),
            lodMax: toNumberOrNull(values.ActivityFactorLodMaxPercent as number | string | null),
        },
        decimalPlaces: decimalPlaces !== null && decimalPlaces >= 0 ? Math.trunc(decimalPlaces) : ACTIVITY_FACTOR_SETTINGS_OFF.decimalPlaces,
    };
}

/**
 * Reads the six activity-factor configuration values in one request. Never throws: a failed read
 * behaves as "feature off", which is the safe default for an opt-in feature.
 */
export async function loadActivityFactorSettings(): Promise<ActivityFactorSettings> {
    try {
        const response = await Axios.get<Record<string, unknown>>(`/Configuration/${SETTING_KEYS.join(',')}`);
        return toActivityFactorSettings(response.data);
    } catch (error) {
        console.warn('Failed to load activity factor configuration; treating the feature as off.', error);
        return ACTIVITY_FACTOR_SETTINGS_OFF;
    }
}

/**
 * The newest other inventory row for the same drug and lot number that carries COA, or null. The
 * lot forms pre-fill from it and warn when the values being entered disagree with it.
 */
export async function fetchLotCoaSibling(drugSource: number, packageId: number, lotNumber: string | null | undefined,
    excludeInventoryId?: number | null): Promise<LotCoaSibling | null> {
    const lot = (lotNumber ?? '').trim();
    if (!lot || !packageId) return null;
    try {
        const response = await Axios.get<LotCoaSibling[]>('/Inventory/lot-coa-siblings', {
            params: { drugSource, packageId, lotNumber: lot, excludeInventoryId: excludeInventoryId || undefined },
        });
        return response.data?.[0] ?? null;
    } catch (error) {
        console.warn('Failed to look up sibling COA for lot', lot, error);
        return null;
    }
}

/** The active formulas in which the drug is a flagged ingredient with a fixed conversion factor. */
export async function fetchFixedFactorIngredientUsages(drugSource: number, packageId: number): Promise<FixedFactorIngredientUsage[]> {
    if (!packageId) return [];
    try {
        const response = await Axios.get<FixedFactorIngredientUsage[]>('/Inventory/fixed-factor-ingredients', {
            params: { drugSource, packageId },
        });
        return response.data ?? [];
    } catch (error) {
        console.warn('Failed to look up fixed-factor ingredient usage', error);
        return [];
    }
}
